import React, { useEffect, useState } from "react";
import { Menu, Avatar, Switch, Dropdown } from "antd";
import {
  DashboardOutlined,
  ContainerOutlined,
  ShopOutlined,
  CreditCardOutlined,
  UndoOutlined,
  SwapOutlined,
  BellOutlined,
  InboxOutlined,
  BarChartOutlined,
  MonitorOutlined,
  DollarOutlined,
  UserOutlined,
  FileImageOutlined,
  CustomerServiceOutlined,
  LineChartOutlined,
  GlobalOutlined,
  LogoutOutlined,
  BgColorsOutlined,
  MoonOutlined,
  SunOutlined,
  UpOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import ModalScreenManagement from "../components/ModalScreenManagement";
// import type { UserRole, Page } from "../../App";

interface SidebarProps {
  userRole: number;
  customClass?: string;
  setTheme?: (theme: "light" | "dark") => void;
  theme?: "light" | "dark";
  onProfile?: () => void;
  onLogout?: () => void;
  /** opens the appearance (theme) picker */
  onAppearance?: () => void;
}

const adminMenuItems = [
  { key: "/", label: "Dashboard", icon: <DashboardOutlined /> },
  {
    key: "lottery",
    label: "Manage Lottery",
    icon: <ContainerOutlined />,
  },
  { key: "organiser", label: "State Lottery Provider", icon: <ShopOutlined /> },
  { key: "users", label: "Users", icon: <UserOutlined /> },

  { key: "notification", label: "Notifications", icon: <BellOutlined /> },
  { key: "backgrounds", label: "backgrounds", icon: <FileImageOutlined /> },
  { key: "revert-sold", label: "Revert Sold Lottery", icon: <UndoOutlined /> },
  { key: "transfer-pack", label: "Transfer Pack", icon: <SwapOutlined /> },
];

const storeMenuItems = [
  { key: "/", label: "Dashboard", icon: <DashboardOutlined /> },
  {
    key: "inventory",
    label: "Inventory Management",
    icon: <ContainerOutlined />,
  },
  { key: "packs", label: "Activate Packs", icon: <InboxOutlined /> },
  { key: "reports", label: "Reports", icon: <BarChartOutlined /> },
  {
    key: "payment-history",
    label: "Payment History",
    icon: <DollarOutlined />,
  },
  { key: "live-display", label: "Live Display", icon: <MonitorOutlined /> },
  { key: "staff-display", label: "Staff Display", icon: <MonitorOutlined /> },
  { key: "profile", label: "Profile", icon: <UserOutlined /> },
];

export const Sidebar: React.FC<SidebarProps> = ({
  userRole,
  // Surface and rules come from the store's theme (theme/AppTheme.tsx), so the
  // rail follows whichever preset is active instead of a fixed off-white.
  customClass = "hidden lg:flex app-surface backdrop-blur-xl shadow-2xl border-r app-border",
  setTheme,
  theme,
  onProfile,
  onLogout,
  onAppearance,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showScreenModal, setShowScreenModal] = useState(false);
  const menuItems = userRole == 1 ? adminMenuItems : storeMenuItems;

  return (
    <>
      <div className={`w-64 flex flex-col h-full ${customClass}`}>
        <div className="p-6 border-b app-border">
          <div className="flex items-center space-x-3">
            <img
              src={`${process.env.PUBLIC_URL}/logo512.png`}
              alt="Logo"
              className="h-10 w-auto object-contain"
            />
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto">
          <Menu
            mode="inline"
            selectedKeys={[location.pathname.split("/")[1] || "/"]}
            items={menuItems}
            onClick={({ key }) => {
              if (key === "staff-display") {
                const newwindow = window.open(
                  "/staff-display",
                  "View Display",
                  "",
                  true,
                );
              } else if (key === "live-display") setShowScreenModal(true);
              else navigate(key);
            }}
            className="border-none bg-transparent"
            style={{
              backgroundColor: "transparent",
              border: "none",
            }}
          />
        </nav>

        <div className="mt-auto border-t app-border">
          <Dropdown
            trigger={["click"]}
            placement="topLeft"
            menu={{
              items: [
                {
                  key: "profile",
                  label: "Profile",
                  icon: <UserOutlined />,
                },
                {
                  key: "support",
                  label: "Support",
                  icon: <CustomerServiceOutlined />,
                },
                ...(onAppearance
                  ? [
                      {
                        key: "appearance",
                        label: "Appearance",
                        icon: <BgColorsOutlined />,
                      },
                    ]
                  : []),
                ...(setTheme
                  ? [
                      {
                        key: "theme",
                        label: theme === "dark" ? "Light mode" : "Dark mode",
                        icon:
                          theme === "dark" ? <SunOutlined /> : <MoonOutlined />,
                      },
                    ]
                  : []),
                { type: "divider" as const },
                {
                  key: "logout",
                  label: "Logout",
                  icon: <LogoutOutlined />,
                  danger: true,
                },
              ],
              onClick: ({ key }) => {
                if (key === "logout") onLogout?.();
                else if (key === "profile") onProfile?.();
                else if (key === "appearance") onAppearance?.();
                else if (key === "theme")
                  setTheme?.(theme === "dark" ? "light" : "dark");
                else console.log(`Clicked: ${key}`);
              },
            }}
          >
            <button
              type="button"
              // gray-100 is lighter than the softened surface, so hover would
              // barely register — tint instead of lighten
              className="w-full flex items-center justify-between px-6 py-4 bg-transparent app-hover transition-colors"
            >
              <div className="flex items-center space-x-3 min-w-0">
                <Avatar
                  size="small"
                  icon={<UserOutlined />}
                  className="bg-gradient-to-br from-emerald-500 to-emerald-600 shadow shrink-0"
                />
                <span className="text-sm font-medium app-muted truncate">
                  Profile
                </span>
              </div>
              <UpOutlined className="text-xs app-muted" />
            </button>
          </Dropdown>
        </div>
      </div>
      <ModalScreenManagement
        showModal={showScreenModal}
        toggleModal={setShowScreenModal}
      />
    </>
  );
};
